import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, X } from "lucide-react";
import { EASE, LogoMark } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What tours do you run?",
  "Price for a group of 15?",
  "What should we bring?",
];

function getSessionId() {
  let id = localStorage.getItem("tai-chat-session");
  if (!id) {
    id = window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("tai-chat-session", id);
  }
  return id;
}

function renderContent(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold text-gold">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );
}

export default function Concierge() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API}/chat/${sessionId.current}`);
      const hist = await res.json();
      if (Array.isArray(hist) && hist.length) {
        setMessages(hist.map((m) => ({ role: m.role, content: m.content })));
      }
    } catch (e) {
      /* fresh chat */
    }
  };

  const toggle = () => {
    if (!open && messages.length === 0) loadHistory();
    setOpen((o) => !o);
  };

  const send = async (preset) => {
    const msg = (preset || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          const data = JSON.parse(line);
          if (data.delta || data.error) {
            const text = data.delta || data.error;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + text };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "The concierge has gone ashore for a moment. Please call +353 86 251 8252 or send an enquiry below.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.8, ease: EASE }}
        onClick={toggle}
        data-testid="concierge-open"
        aria-label="Ask the concierge"
        className="fixed bottom-5 left-5 z-[75] flex items-center gap-3 rounded-full border border-gold/40 bg-navy-800/90 py-3 pl-4 pr-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-ivory shadow-2xl shadow-navy-950 backdrop-blur-xl transition-colors duration-300 hover:border-gold"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold" />
        </span>
        Ask the Concierge
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.4, ease: EASE }}
            data-testid="concierge-panel"
            className="fixed bottom-20 left-5 right-5 z-[75] flex max-h-[70vh] flex-col border border-gold/25 bg-navy-900/95 shadow-2xl shadow-navy-950 backdrop-blur-xl sm:right-auto sm:w-[400px]"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div className="flex items-center gap-3">
                <img src="/images/logo-dark.png" alt="The Adventure Islands" className="h-9 w-auto" />
                <div>
                  <p className="font-serif text-lg leading-tight text-ivory">Ask the Concierge</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-mist">Powered by ChatGPT</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="concierge-close"
                aria-label="Close concierge"
                className="border border-white/15 p-2 text-ivory/70 transition-colors hover:border-gold hover:text-gold"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="concierge-messages">
              {messages.length === 0 && (
                <div>
                  <p className="font-serif text-lg italic leading-snug text-ivory/90">
                    Failte! Planning a day on Clew Bay?
                  </p>
                  <p className="mt-2 text-[13px] font-light leading-relaxed text-mist">
                    Ask me about tours, prices, groups or what to bring &mdash; or send an
                    enquiry below and we'll build your adventure.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid={`concierge-suggestion-${SUGGESTIONS.indexOf(s) + 1}`}
                        className="border border-gold/30 px-3 py-2 text-[11px] tracking-wide text-gold transition-colors hover:bg-gold-muted"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={m.role === "user" ? `concierge-message-user-${i}` : `concierge-message-ai-${i}`}
                  className={
                    m.role === "user"
                      ? "ml-auto max-w-[85%] bg-gold-muted px-4 py-3 text-[13px] leading-relaxed text-ivory"
                      : "max-w-[90%] border-l-2 border-gold/60 pl-4 text-[13px] font-light leading-relaxed text-ivory/90"
                  }
                >
                  {m.content ? (
                    m.role === "assistant" ? renderContent(m.content) : m.content
                  ) : (
                    <span className="flex gap-1 py-1" aria-label="Concierge is typing">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold [animation-delay:0ms]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold [animation-delay:300ms]" />
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 p-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
                className="flex items-center gap-3"
                data-testid="concierge-form"
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about tours, prices, groups..."
                  data-testid="concierge-input"
                  className="w-full border-b border-white/20 bg-transparent py-2.5 text-sm text-ivory placeholder:text-mist/50 focus:border-gold focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={streaming || !input.trim()}
                  data-testid="concierge-send"
                  aria-label="Send message"
                  className="flex h-10 w-10 shrink-0 items-center justify-center bg-gold text-navy-800 transition-colors hover:bg-gold-light disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
